import { useEffect, useState } from "react";
import { BACKEND_URL } from "../../hooks/useOasisBackend";
import {
    EstimatedCharacteristicsReport,
    SchemaItem,
    normalizeCharacteristicAnswer,
    serializeCharacteristicAnswerForSubmit,
} from "../types";
import Modal from "./ui/Modal";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useUser } from "../../hooks/useUser";


interface CharacteristicEstimationCorrectionProps {
    simulation_token: string | null;
    estimatedCharacteristicsReport: EstimatedCharacteristicsReport | null;
    answer: string | number | boolean | null;
    explanation: string;
    confidence: number | null;
    importance: number | null;
    characteristicKey: string;
    characteristicSchema?: SchemaItem;
    onClose: () => void;
    onSuccess: (
        characteristicKey: string,
        field: 'answer' | 'explanation' | 'confidence' | 'importance' | 'corrected_by' | 'comment' | 'correction_count',
        value: string | number | null
    ) => void;
    isOpen: boolean;
}

const CharacteristicEstimationCorrectionModal = ({ onSuccess, simulation_token, estimatedCharacteristicsReport, onClose, answer, explanation, confidence, importance, characteristicKey, characteristicSchema, isOpen }: CharacteristicEstimationCorrectionProps) => {
    const [editedAnswer, setEditedAnswer] = useState<string>(() => normalizeCharacteristicAnswer(answer));
    const [editedConfidence, setEditedConfidence] = useState<number | null>(confidence ?? null);
    const [editedImportance, setEditedImportance] = useState<number | null>(importance ?? null);
    const [comment, setComment] = useState<string>("");
    const user = useUser();

    useEffect(() => {
        if (!isOpen) return;
        setEditedAnswer(normalizeCharacteristicAnswer(answer));
        setEditedConfidence(confidence ?? null);
        setEditedImportance(importance ?? null);
        setComment("");
    }, [isOpen, answer, confidence, importance]);

    const submitCharacteristicEstimationCorrection = () => {
        const submittedAnswer = serializeCharacteristicAnswerForSubmit(
            editedAnswer,
            characteristicSchema?.type,
        );
        const characteristicEstimationCorrection = {
            simulation_token: simulation_token,
            estimation: {
                answer: submittedAnswer,
                explanation: explanation,
                confidence: editedConfidence,
                importance: editedImportance !== null ? Math.round(editedImportance) : 0,
            },
            comment: comment,
            characteristic_event_id: estimatedCharacteristicsReport?.characteristic_event_id || null,
            characteristic_key: characteristicKey,
        };

        fetch(`${BACKEND_URL}/vnog/create_characteristic_estimation/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': Cookies.get('csrftoken') || '',
            },
            credentials: 'include',
            body: JSON.stringify(characteristicEstimationCorrection),
        }).then((response) => {
            if (response.status === 200) {
                toast.success('Characteristic estimation correction submitted successfully');
                onSuccess(characteristicKey, 'answer', normalizeCharacteristicAnswer(submittedAnswer));
                onSuccess(characteristicKey, 'confidence', editedConfidence);
                onSuccess(characteristicKey, 'importance', editedImportance);
                if (user.state === 'LoggedIn') {
                    onSuccess(characteristicKey, 'corrected_by', user.user.email || '');
                }
                onSuccess(characteristicKey, 'comment', comment);
                const currentCount = estimatedCharacteristicsReport?.characteristics?.[characteristicKey]?.correction_count ?? 0;
                onSuccess(characteristicKey, 'correction_count', currentCount + 1);
                onClose();
            } else {
                toast.error('Error submitting characteristic estimation correction');
            }
        }).catch((error) => {
            toast.error('Error submitting characteristic estimation correction');
        });
    };

    const renderSchemaDrivenAnswerInput = () => {
        if (!characteristicSchema) {
            return (
                <input
                    type="text"
                    value={editedAnswer}
                    onChange={(e) => setEditedAnswer(e.target.value)}
                    className="w-full p-1 border border-gray-300 rounded"
                />
            );
        }

        switch (characteristicSchema.type) {
            case 'Getal':
                return (
                    <input
                        type="number"
                        step="any"
                        value={editedAnswer}
                        onChange={(e) => setEditedAnswer(e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    />
                );
            case 'Meervoudige opsom':
            case 'Enkelvoudige opsom':
                return (
                    <select
                        value={editedAnswer}
                        onChange={(e) => setEditedAnswer(e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    >
                        <option value="">Select an option</option>
                        {characteristicSchema.answer_options?.map((option: string) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                );
            case 'Ja/Nee':
                return (
                    <select
                        value={editedAnswer}
                        onChange={(e) => setEditedAnswer(e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    >
                        <option value="">Select an option</option>
                        <option value="Ja">Ja</option>
                        <option value="Nee">Nee</option>
                    </select>
                );
            case 'date':
                return (
                    <input
                        type="date"
                        value={editedAnswer}
                        onChange={(e) => setEditedAnswer(e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        value={editedAnswer}
                        onChange={(e) => setEditedAnswer(e.target.value)}
                        className="w-full p-1 border border-gray-300 rounded"
                    />
                );
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
        >
                <div className="bg-white relative z-100 rounded-xl shadow-2xl p-8 md:p-10 w-[92vw] h-[85vh] overflow-hidden flex flex-col">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
                            aria-label="Close"
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                strokeWidth="2"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        <div className="flex items-start justify-between gap-8 flex-1 overflow-hidden">
                            <div className="flex-1 min-w-0 bg-gradient-to-br from-indigo-50 to-white rounded-lg p-6 border border-indigo-100">
                                <h1 className="text-3xl font-semibold tracking-tight text-gray-900">Correct the estimation</h1>
                                <div className="mt-6 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Answer</label>
                                        <div className="mt-1">{renderSchemaDrivenAnswerInput()}</div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Confidence ({editedConfidence !== null ? Math.round((editedConfidence as number) * 100) : 0}%)</label>
                                        <input
                                            type="range"
                                            min={0}
                                            max={100}
                                            step={1}
                                            value={editedConfidence !== null ? Math.round((editedConfidence as number) * 100) : 0}
                                            onChange={(e) => setEditedConfidence(Number(e.target.value) / 100)}
                                            className="mt-1 w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Importance ({editedImportance ?? 0})</label>
                                        <input
                                            type="range"
                                            min={0}
                                            max={10}
                                            step={1}
                                            value={editedImportance ?? 0}
                                            onChange={(e) => setEditedImportance(Number(e.target.value))}
                                            className="mt-1 w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Comment (optional)</label>
                                        <textarea
                                            value={comment || ''}
                                            onChange={(e) => setComment(e.target.value)}
                                            className="mt-1 w-full p-2 border border-gray-300 rounded"
                                            rows={3}
                                        />
                                    </div>
                                    <div className="pt-2">
                                        <button
                                            onClick={submitCharacteristicEstimationCorrection}
                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
                                        >
                                            Submit correction
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
        </Modal>
    );
};

export default CharacteristicEstimationCorrectionModal;